/**
 * EventDetailPage — the AthloHive event detail page, rendered by React
 * components instead of the Page Builder record template.
 *
 * It stands exactly where the template stood: same route (/p/event/:recordId),
 * same endpoint, same record and related blocks, same action buttons and
 * forms. Only the renderer changes — see EventDetailTemplate for the layout and
 * styles/eventDetail.css for the stylesheet, exported from the page as-is.
 *
 * Everything the generic page does around the layout is done here too, because
 * a visitor would notice each one going missing: SEO from the record, app
 * branding, the breadcrumb named after the event, the loading and error states,
 * and the success modal that carries a pass code.
 */

import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { usePublicRecordDetail } from '../hooks/usePublicTableView';
import usePublicAction from '../hooks/usePublicAction';
import { applyPageSEO, resolveSeoForRecord } from '../utils/seoHelper';
import { usePublicApp, useCrumbLabel } from '../context/PublicAppContext';
import { buildRelatedScope } from '../utils/relatedScope';
import { buildCheckoutPayload, isPaidCheckout } from '../components/athlohive/event-detail/buildCheckoutPayload';
import {
  startCheckout, fetchGatewayConfig, launchCheckout, fetchPaymentStatus,
} from '../components/athlohive/event-detail/checkoutClient';
import useCurrencies from '../hooks/useCurrencies';
import { resolveEventCurrency, formatAmount } from '../components/athlohive/currencyCatalog';
import { eventButtons } from '../components/athlohive/event-detail/eventButtons';
import { registrationRules } from '../components/athlohive/event-detail/registrationRules';
import {
  buildRegistrationBatch, passesFromResult, seatName, REGISTRATION_TABLE, userInfoOf,
  attendeeNameOf,
} from '../components/athlohive/event-detail/buildRegistrationBatch';
import {
  rememberEntered, recallEntered, forgetEntered, belongsTo,
} from '../components/athlohive/event-detail/registrationMemory';
import {
  rememberInFlight, recallInFlight, forgetInFlight,
} from '../components/athlohive/event-detail/inFlightCheckout';
import {
  fetchMyRegistrations, holdsAnEntry,
} from '../components/athlohive/event-detail/fetchMyRegistrations';
import {
  fetchActivityAvailability,
} from '../components/athlohive/event-detail/fetchActivityAvailability';
import { checkInOpensHint } from '../components/athlohive/event-detail/selfCheckInRules';
import {
  recallPending, forgetPending, settlementOf,
} from '../components/athlohive/event-detail/pendingPayment';
import { isUserLoggedIn } from '../utils/utils';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';

import { decideCompleted } from '../utils/actionCompletion';
import { startAction, buildSignInUrl } from '../utils/actionRunner';
import PublicSuccessModal from '../components/public/PublicSuccessModal';
import EventDetailSkeleton from '../components/athlohive/event-detail/EventDetailSkeleton';
import PublicErrorPage from '../components/public/PublicErrorPage';
import EventDetailTemplate from '../components/athlohive/event-detail/EventDetailTemplate';
import EventEntryModal from '../components/athlohive/event-detail/EventEntryModal';
import { formatValue, eventTitleOf } from '../components/athlohive/event-detail/eventDetailScope';

// The batch endpoint is an application API rather than a public one, so it
// needs the same client the organizer's event wizard uses.
const registrationApi = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/**
 * Turns one batch reply into the per-seat report the modal renders.
 *
 * The modal's shape is unchanged — a result per seat — because that is what it
 * shows and what EventPassViewer reads. What changed is that every seat now
 * shares ONE outcome: all of them succeeded, or none of them did.
 */
function outcomeFromBatch(resp, result, requests) {
  if (!resp.ok || !result || !Array.isArray(result.operations)) {
    // The server's own sentence when it sent one — "This event is full",
    // "You have already registered" — because that is the only text that tells
    // a visitor what actually went wrong.
    const failed = (result && result.failed_operation) || {};
    const message = (result && result.error)
      || `The server could not complete this (error ${resp.status}).`;

    // A unique rule on the registration table means one thing to a visitor,
    // whichever of the two it was: they already hold an entry for this event.
    // The database says so in terms of columns — "Event Activity Id, Created
    // By must be unique" — which is true and useless to read.
    const alreadyEntered = failed.reason === 'constraint_violation'
      && failed.table === REGISTRATION_TABLE;

    const reason = failed.reason === 'row_limit_full'
      ? 'There are no places left for one of the activities you chose, so nothing was booked.'
      : alreadyEntered
        ? 'You have already registered for this event.'
        : message;

    return requests.map(seat => ({
      id: `${seat.activity.id}:${seat.seatNo}`,
      name: seatName(seat),
      seatNo: seat.seatNo,
      seatsForActivity: seat.seatsForActivity,
      result: { ok: false, message: reason, alreadyEntered },
    }));
  }

  const passes = passesFromResult(result, requests);

  return requests.map((seat, index) => ({
    id: `${seat.activity.id}:${seat.seatNo}`,
    name: seatName(seat),
    seatNo: seat.seatNo,
    seatsForActivity: seat.seatsForActivity,
    // Who the booker named for this seat, if anyone — the pass viewer's label.
    attendeeName: attendeeNameOf(seat),
    result: {
      ok: true,
      message: 'You are registered.',
      // A waiting-list seat produced no pass, and that is a success with
      // nothing to show at a gate rather than a failure.
      reveal: passes[index]
        ? {
            fields: passes[index].fields,
            // The modal reads `qr.svg` — a drawn picture, which is what the
            // server returns for this column.
            qr: { label: 'Show QR at the gate', svg: passes[index].qrSvg },
          }
        : null,
    },
  }));
}

// The table this page draws. The record sits under its table's own name in the
// scope, exactly as it did for the template, so the bindings are unchanged.
export const EVENT_TABLE_SLUG = 'event';

/* How long the event page keeps asking after a noted payment, and how fast.
   Widening, and deliberately short: the return page is where a runner is meant
   to wait, and this is only for one who wandered back here instead. It stops
   rather than polling forever — a payment still unresolved after this is left
   to the note's own expiry. */
const PENDING_POLL_MS = [2000, 3000, 5000, 8000];

export default function EventDetailPage() {
  const { tableSlug = EVENT_TABLE_SLUG, recordId } = useParams();
  const [searchParams] = useSearchParams();
  const previewKey = searchParams.get('pk') || undefined;

  const { data, isLoading, isError, error, refetch } =
    usePublicRecordDetail(tableSlug, recordId, { previewKey });
  const { setAppBranding } = usePublicApp();

  const viewConfig = data?.view_config || {};
  const record = data?.record || {};
  const actionButtons = viewConfig.action_buttons;

  useEffect(() => {
    if (data?.view_config?.seo) {
      const mapping = data.view_config.field_mapping || {};
      // const titleValue = data.record?.[mapping.title_field];
      // The name the heading shows — see eventTitleOf in eventDetailScope.js.
      const titleValue = eventTitleOf(data.record, mapping);
      const branding = data.view_config.branding || {};

      const resolvedSeo = resolveSeoForRecord(data.view_config.seo, data.record);
      applyPageSEO({
        ...resolvedSeo,
        meta_title: titleValue
          ? `${titleValue} | ${branding.page_title || ''}`
          : resolvedSeo.meta_title,
        og_title: titleValue || resolvedSeo.og_title,
      }, data.app_branding);
    }
  }, [data]);

  useEffect(() => {
    if (data?.app_branding) setAppBranding(data.app_branding);
  }, [data?.app_branding, setAppBranding]);

  // The trail ends with the event's name rather than its id.
  // useCrumbLabel(record?.[viewConfig?.field_mapping?.title_field]);
  useCrumbLabel(eventTitleOf(record, viewConfig?.field_mapping));

  const {
    signedIn, actionState, setActionState, completedKeys,
    submitAction, resumeAfterSignIn,
  } = usePublicAction({ tableSlug, recordId, actionButtons });

  // What the visitor filled in, held while they choose their activities. The
  // form is answered once and counts for every activity they enter.
  // Above every early return below, because hooks cannot be called
  // conditionally. The catalog is platform-wide and react-query shares one
  // cache entry, so EventDetailTemplate's own call costs no extra request.
  const { catalog: currencies } = useCurrencies();

  const [pending, setPending] = useState(null);

  // The registration rules, held in a ref because they are derived below the
  // loading guard while confirmEntry is defined above it. A ref rather than
  // state: the modal reads them at the moment of submission, and re-rendering
  // when they change would serve no purpose.
  const rulesRef = useRef(null);

  // Whether this visitor has already entered this event.
  //
  // TWO SOURCES, AND THEY ANSWER DIFFERENT QUESTIONS. Browser storage is
  // instant and needs no network, but it is per-device: a cleared cache or a
  // second phone forgets. The server is asked as well, because that answer
  // survives both — it comes from the rows themselves.
  //
  // The storage answer is applied first so a returning visitor never sees the
  // button flash into view before the fetch lands. The server can only turn it
  // ON: it never clears a marker, because a fetch that could not answer
  // (no read permission yet, a flaky network) returns null, and treating that
  // as "not registered" would put the button back in front of somebody who is.
  const [entered, setEntered] = useState(false);

  // Set when the visitor IS signed in but the lookup could not answer. Held
  // apart from `entered` because the three states need different screens: a
  // known entry, a known absence, and "we could not tell". Offering the button
  // on the third would let a registered person start an entry that the
  // database is going to refuse.
  const [statusUnknown, setStatusUnknown] = useState(false);

  // A payment for THIS event that left for the gateway and has not been heard
  // back on. Held apart from `entered` because it is a weaker claim: not "you
  // are in" but "you started paying and we do not know the end of it yet".
  const [pendingPayment, setPendingPayment] = useState(false);

  // The signed-in account, held only to PREFILL the checkout's contact fields.
  // Null for an anonymous visitor, which simply means the fields start empty.
  const [session, setSession] = useState(null);
  useEffect(() => {
    let cancelled = false;
    isUserLoggedIn()
      .then(who => { if (!cancelled) setSession(who || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);


  // A failure we have already recorded for this event, held in a ref so that a
  // LATER reading of "nobody is signed in" cannot quietly erase it.
  //
  // That is not hypothetical. A 401 from the lookup makes APIHelper refresh the
  // token, retry, and — still refused — clear the token and throw. The next
  // pass therefore sees no signed-in user and took the signed-out branch below,
  // which reset the failure and put the Register button back in front of
  // somebody who may already hold a place. Only a successful answer clears it.
  const sawFailureRef = useRef(false);

  const checkRegistration = useCallback(async () => {
    const id = record?.id ?? recordId;
    if (!id) return;

    const who = await isUserLoggedIn().catch(() => null);
    // Signed out: there is nothing to look up, and no failure to report — UNLESS
    // we already recorded one, in which case this reading is the consequence of
    // that failure rather than an independent fact about the visitor.
    if (!who?.user_id) {
      if (!sawFailureRef.current) setStatusUnknown(false);
      return;
    }

    const count = await fetchMyRegistrations({ eventId: id, applicationUserId: who.user_id });
    if (holdsAnEntry(count)) {
      setEntered(true);
      sawFailureRef.current = false;
      setStatusUnknown(false);
      rememberEntered(envConfig.organizationId, envConfig.applicationId, id, count, who.user_id);
      return;
    }
    // A number means the question was answered: zero rows, not registered.
    // null means it could not be asked at all.
    sawFailureRef.current = count === null;
    setStatusUnknown(count === null);

    // Answered, and the answer is "no active entry" — so a marker saying
    // otherwise is stale and has to go. This is what lets somebody whose
    // registration was CANCELLED (a refund, an organizer cancellation) register
    // again: the server stopped counting them long ago, and until now only this
    // browser's marker kept insisting they were in.
    //
    // Guarded on `count !== null` deliberately. A lookup that FAILED must never
    // clear the marker — that would offer a second seat to somebody who already
    // holds one, every time the network hiccups.
    if (count !== null) {
      forgetEntered(envConfig.organizationId, envConfig.applicationId, id);
      setEntered(false);
    }
  }, [record?.id, recordId]);

  /**
   * Resolves a payment this browser noted as unfinished.
   *
   * The note is written by the return page while a payment is unresolved — see
   * pendingPayment for why it has to exist at all: a paid entry writes no rows
   * until the webhook lands, so until then this page has nothing to go on and
   * would offer Register to somebody who has already paid once.
   *
   * IT IS CHECKED AGAINST WHOEVER IS SIGNED IN, exactly as the entered marker
   * is. localStorage outlives a logout — the sign-out handler clears the
   * session, not this — so without that check the next person to use this
   * browser would be shown the last person's payment, and refused the button
   * for an event they have never touched. A note that cannot be shown to be
   * theirs is removed rather than merely ignored, so it stops being asked
   * about on every load.
   *
   * IT KEEPS ASKING while the answer is "not yet". A runner who leaves the
   * event page open through a slow webhook should watch the button become
   * their entry, not sit in front of a stale screen until they think to
   * reload. The attempts widen and then stop: this is a courtesy, not a
   * heartbeat, and the note's own expiry is what covers a payment that never
   * resolves at all.
   */
  useEffect(() => {
    const id = record?.id ?? recordId;
    if (!id) return undefined;

    const note = recallPending(envConfig.organizationId, envConfig.applicationId, id);
    if (!note) return undefined;

    // Shown first, verified second. The note is what this browser knows right
    // now, and a runner who has just paid should not watch Register sit there
    // while a round trip decides whether to withdraw it.
    setPendingPayment(true);

    let alive = true;
    let timer = null;

    const stop = (settled) => {
      forgetPending(envConfig.organizationId, envConfig.applicationId, id);
      setPendingPayment(false);
      // Confirmed means rows now exist, so the ordinary lookup can see them.
      // Asked rather than assumed, so `entered` is set by the same path that
      // sets it everywhere else on this page.
      if (settled === 'confirmed') checkRegistration();
    };

    (async () => {
      const who = await isUserLoggedIn().catch(() => null);
      if (!alive) return;

      // Not ours — or nobody is signed in, or the note predates ids. Any of
      // those and it cannot be shown to belong to this visitor.
      if (!belongsTo(note, who?.user_id)) {
        forgetPending(envConfig.organizationId, envConfig.applicationId, id);
        setPendingPayment(false);
        return;
      }

      const ask = async (attempt) => {
        const status = await fetchPaymentStatus({
          applicationId: envConfig.applicationId,
          gatewayOrderId: note.orderId,
        }).catch(() => null);
        if (!alive) return;

        const settled = settlementOf(status);
        if (settled) { stop(settled); return; }

        // Unresolved, or unanswerable. The note stands — clearing it on silence
        // is how somebody who really did pay gets offered the button again.
        if (attempt >= PENDING_POLL_MS.length) return;
        timer = setTimeout(() => ask(attempt + 1), PENDING_POLL_MS[attempt]);
      };

      ask(0);
    })();

    return () => { alive = false; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id, recordId]);

  useEffect(() => {
    // WAIT FOR THE RECORD. This used to run the moment the component mounted,
    // using the id from the URL, and then run a SECOND time when the fetched
    // record arrived and `record.id` replaced it — two identical
    // table/read/records/ lookups for the same event on every page load. That
    // is not StrictMode: it happened with StrictMode off too.
    //
    // Nothing is lost by waiting. The render below returns EventDetailSkeleton
    // until `isLoading` clears, so there is no button on screen to flash and
    // nothing for the marker to paint over until this point anyway.
    if (!data) return undefined;

    const id = record?.id ?? recordId;
    if (!id) return undefined;

    // PAINTED FIRST, VERIFIED SECOND. The marker is read synchronously so a
    // returning visitor never sees the button flash into view — that is the
    // whole reason it exists. It is then checked against whoever is actually
    // signed in, because the marker says only that THIS BROWSER entered, not
    // who did: after a logout, or a session that quietly expired, the next
    // person was being shown someone else's entry and offered no way in.
    const marker = recallEntered(envConfig.organizationId, envConfig.applicationId, id);
    if (marker) setEntered(true);

    let alive = true;

    (async () => {
      const who = await isUserLoggedIn().catch(() => null);
      if (!alive) return;

      // Nobody signed in. A marker cannot be shown to belong to this visitor,
      // so it goes and the button comes back. Nothing to look up either.
      if (!who?.user_id) {
        if (marker) {
          forgetEntered(envConfig.organizationId, envConfig.applicationId, id);
          setEntered(false);
        }
        // Same guard as checkRegistration: a token cleared by a refused lookup
        // makes a signed-in person read as signed-out on the next pass, and
        // that must not wipe the failure we already recorded.
        if (!sawFailureRef.current) setStatusUnknown(false);
        return;
      }

      // Ours. Paint the entered state straight away — that is what the marker
      // is for — but do NOT stop here.
      //
      // This used to return. A marker records that a registration was once
      // written; it cannot know one was since CANCELLED by a refund or by the
      // organizer, and the server is never asked again on this path. That left
      // a refunded visitor looking at "You have already registered" with no way
      // back in, on that browser, permanently. So the marker now decides WHEN
      // the state appears, not WHETHER it is true — checkRegistration below is
      // the authority and will clear it if the entry is gone.
      if (belongsTo(marker, who.user_id)) {
        setEntered(true);
      } else if (marker) {
        // Somebody else's, or written before markers carried an id. Drop it and
        // let the server answer for the person actually signed in.
        forgetEntered(envConfig.organizationId, envConfig.applicationId, id);
        setEntered(false);
      }

      await checkRegistration();
    })().catch(() => {
      // checkRegistration handles its own faults; this only stops an unhandled
      // rejection if that ever stops being true.
      if (alive) setStatusUnknown(true);
    });
    return () => { alive = false; };
    // `data` gates the effect above; react-query keeps one stable reference per
    // fetched result, so adding it here does not make this run more often.
  }, [data, record?.id, recordId, checkRegistration]);

  // Register does not write any more — it asks which activities first. The
  // answers travel unchanged into whichever writes follow.
  const openEntryModal = useCallback((submission) => setPending(submission), []);

  /**
   * Open the SAME modal for somebody who already holds an entry.
   *
   * `bookingMore` is the whole difference, and it is deliberately an explicit
   * flag rather than something inferred from `entered`: confirmEntry refuses a
   * second booking on purpose, and that refusal protects every path where a
   * duplicate is an accident — a stale page kept open, a back-button return, a
   * re-submission after signing in. Inferring the intent would disarm it for
   * all of them. Saying it here disarms it only for the button a visitor
   * pressed meaning exactly this.
   *
   * It does not go through startAction. That exists to send a signed-out
   * visitor to sign in and bring them back, and nobody reaches this button
   * without a session — `entered` is only ever set after the server has
   * confirmed an entry for the account that is signed in.
   */
  const openMoreSeats = useCallback(button => openEntryModal({
    button, answers: {}, payload: {}, bookingMore: true,
  }), [openEntryModal]);

  /**
   * Writes the whole checkout in ONE call.
   *
   * Every seat of every chosen activity goes in a single batch, so the trip
   * that used to be one request per seat — paced apart to stay under the burst
   * limit, and reported a line at a time — is now one request and one outcome.
   *
   * THE TRADE, stated because the modal has to show it: a batch is
   * all-or-nothing. If one activity is full, every seat in the call is rolled
   * back, including the ones that would have been fine. The old per-seat loop
   * could say "the 21K saved, the yoga was full"; this cannot, and the report
   * says so instead of implying a partial success that did not happen.
   */
  /**
   * Hands a paid selection to the gateway.
   *
   * Returns `undefined` on success, and that is deliberate: the browser is
   * navigating to the gateway's hosted page, so there is no outcome to report
   * and the modal must not flash a result list on the way out. Only a refusal
   * returns rows.
   *
   * Nothing is remembered here either. `rememberEntered` belongs on the return
   * page, after the payment is CONFIRMED -- writing it now would withdraw the
   * Register button from a runner who then abandoned the payment and holds no
   * place at all.
   */
  const startPaidCheckout = useCallback(async (requests, options, who, eventId) => {
    const refuse = (message) => requests.map(seat => ({
      id: `${seat.activity.id}:${seat.seatNo}`,
      name: seatName(seat),
      seatNo: seat.seatNo,
      seatsForActivity: seat.seatsForActivity,
      result: { ok: false, message },
    }));

    let body;
    try {
      // The number TYPED AT CHECKOUT, not the one on the account.
      //
      // userInfoOf(who) reads the signed-in profile, and a runner entering
      // on a borrowed phone — or one whose account simply has no number —
      // would otherwise leave a pass with no way to reach them. The typed
      // number is the one they just confirmed for this entry, so it wins,
      // and it lands in the pass's own user_info alongside name and email.
      const typedMobile = (options.customer?.customer_phone_national || '')
        .replace(/\D/g, '');
      const typedCode = (options.customer?.customer_phone_country_code || '').trim();

      body = buildCheckoutPayload(requests, {
        eventId,
        rules: rulesRef.current,
        applicationUserId: who?.user_id,
        userInfo: {
          ...(userInfoOf(who) || {}),
          ...(typedMobile ? { mobile_number: typedMobile } : {}),
          ...(typedMobile && typedCode ? { country_code: typedCode } : {}),
        },
        idempotencyKey: options.idempotencyKey,
        // Applied in the modal, verified again server-side. Undefined when
        // the runner entered no code, which leaves the body unchanged.
        couponCode: options.couponCode,
        // Cashfree substitutes the real order id into the placeholder the
        // driver appends, so this page learns which order it is looking at
        // without ever having had a payment id to embed.
        returnUrl: `${window.location.origin}/checkout/return`,
        customer: {
          customer_phone: [
            options.customer?.customer_phone_country_code,
            options.customer?.customer_phone_national,
          ].filter(Boolean).join(''),
        },
      });
    } catch (err) {
      throw err;   // ours, nothing was sent — the modal keeps the choice
    }

    const gateway = await fetchGatewayConfig({});
    if (!gateway) {
      return refuse('Payments are not set up for this event yet. Please try again later.');
    }

    const started = await startCheckout(body);

    if (!started.ok) {
      if (started.status === 401 || started.status === 403) {
        window.location.href = buildSignInUrl(tableSlug, recordId, pending.button.id);
        return [];
      }
      // Each of these means something different to a runner, and a single
      // "something went wrong" would tell them to do the wrong thing.
      // THE PLACES WENT WHILE THEY WERE DECIDING, and the server said so
      // BEFORE charging anything — which is the whole point of it reserving
      // at checkout. Worth its own sentence because it is the one refusal here
      // that is nobody's fault and that the runner can still act on: the
      // number tells them whether to come back with a smaller selection.
      if (started.error === 'insufficient_capacity') {
        const left = started.payload?.seats_remaining;
        const remaining = typeof left === 'number' ? left : null;
        // EVERY VERSION OPENS WITH "NO PAYMENT HAS BEEN TAKEN", because that
        // is the first thing a runner needs to know: they pressed Pay, the
        // screen changed, and until told otherwise they will assume they have
        // been charged. Saying it first also stops them retrying to "check".
        //
        // The count comes from the server, which measured it under the same
        // lock that refused the booking, so it is accurate at the moment of
        // refusal rather than an estimate.
        return refuse(
          remaining === 0
            ? 'No payment has been taken. This activity is now full.'
            : remaining === null
              ? 'No payment has been taken. These places are no longer '
                + 'available. Please review the activity and try again.'
              : `No payment has been taken. ${remaining} `
                + `${remaining === 1 ? 'place remains' : 'places remain'} on this `
                + 'activity. Please adjust your selection and try again.'
        );
      }

      const message = started.error === 'request_in_progress'
        ? 'This payment is already being started. Give it a moment, then check your entries.'
        : started.error === 'idempotency_key_reuse'
          ? 'Your selection changed. Please close this and choose again.'
          : started.error === 'idempotency_key_expired'
            ? 'This checkout timed out. Please choose your places again.'
            : started.detail || 'The payment could not be started. Please try again.';
      return refuse(message);
    }

    try {
      // RECORDED BEFORE LEAVING, never after: the handover navigates away, so
      // any line written below it may simply not run. See inFlightCheckout --
      // this is what lets a back button give the places back at once instead
      // of leaving them held for a payment that is not coming.
      rememberInFlight(started.payload?.payment_id);
      await launchCheckout({
        providerKey: gateway.provider_key,
        environment: gateway.environment,
        checkoutPayload: started.payload?.provider_checkout_payload,
      });
      // Navigating away. No outcome — see the note above.
      return undefined;
    } catch (err) {
      // Nothing left this tab, so there is no return to act on.
      forgetInFlight();
      // The order exists and is payable; only the handover failed. Say so
      // rather than implying the money is gone.
      return refuse('We could not open the payment page. Please try again.');
    }
  }, [pending, recordId, tableSlug]);

  /**
   * How many places each picked activity still has, asked at the press of
   * Continue and nowhere else.
   *
   * WHY HERE AND NOT ON PAGE LOAD. The count is only worth having at the moment
   * it is acted on: fetched when the page opened it would be minutes stale by
   * the time anyone pressed anything, and would have cost every visitor a
   * request whether or not they ever opened the modal. This runs once per press
   * of a cart the modal has not already settled.
   *
   * It returns null on every failure, and the modal treats null as "carry on" —
   * so a refused read or a dropped connection leaves the entry flow exactly as
   * it was before this existed.
   */
  const checkAvailability = useCallback(
    activities => fetchActivityAvailability({
      eventId: record?.id ?? recordId,
      activities,
    }),
    [record?.id, recordId],
  );

  const confirmEntry = useCallback(async (requests, options = {}) => {
    if (!pending) return [];
    const eventId = record?.id ?? recordId;

    // ASKED AGAIN, HERE, RATHER THAN TRUSTED FROM PAGE LOAD.
    //
    // The check on mount can be stale or can have failed outright — no read
    // permission, a dropped connection, a session that went while the page sat
    // open — and every one of those shows the button to somebody who already
    // holds a place. Re-asking at the moment of the write shrinks that window
    // from "any fault since the page loaded" to the instant before the call.
    //
    // NOT A SUBSTITUTE FOR A RULE IN THE DATABASE. Two tabs still race this,
    // and a request sent straight to the endpoint never runs it at all. It
    // narrows the gap; only the server can close it.
    //
    // A null answer means the question could not be asked, and it must NOT be
    // read as "already registered" — that would refuse a first-time visitor the
    // moment the network hiccuped.
    //
    // `fresh` is the whole point of this call. isUserLoggedIn now serves other
    // callers from a short-lived cache so a page load makes one request instead
    // of four; a cached answer HERE would reintroduce exactly the stale window
    // this re-check exists to close.
    const who = await isUserLoggedIn(undefined, { fresh: true }).catch(() => null);

    // SKIPPED ONLY FOR A SECOND BOOKING THE VISITOR ASKED FOR.
    //
    // Everything the block below protects against is an ACCIDENT — the same
    // cart sent twice, a stale page, a resumed submission. None of those set
    // this flag, because only the "Book more seats" button does. Holding an
    // entry is not a reason to refuse somebody who pressed a button that says
    // what it will do: there is no rule, here or in the database, that one
    // person may hold only one place.
    const bookingMore = Boolean(pending.bookingMore);

    if (who?.user_id && !bookingMore) {
      const held = await fetchMyRegistrations({
        eventId, applicationUserId: who.user_id,
      });
      if (holdsAnEntry(held)) {
        setEntered(true);
        rememberEntered(envConfig.organizationId, envConfig.applicationId, eventId, held, who.user_id);
        return requests.map(seat => ({
          id: `${seat.activity.id}:${seat.seatNo}`,
          name: seatName(seat),
          seatNo: seat.seatNo,
          seatsForActivity: seat.seatsForActivity,
          result: {
            ok: false,
            alreadyEntered: true,
            message: 'You have already registered for this event. ',
          },
        }));
      }

      // Could not tell. Refusing here costs a retry; writing blind can mint a
      // second entry for somebody who already holds one.
      if (held === null) {
        return requests.map(seat => ({
          id: `${seat.activity.id}:${seat.seatNo}`,
          name: seatName(seat),
          seatNo: seat.seatNo,
          seatsForActivity: seat.seatsForActivity,
          result: {
            ok: false,
            message: 'Something went wrong while checking your registration. '
              + 'Please try again in a moment.',
          },
        }));
      }
    }

    // ── PAID: the money moves before anything is written ────────────────
    //
    // Nothing is created here. The seats' rows travel inside the payment and
    // are written by the gateway's webhook once it confirms, so an abandoned
    // checkout leaves no half-registration and holds no capped place.
    if (isPaidCheckout(requests)) {
      return startPaidCheckout(requests, options, who, eventId);
    }

    let body;
    try {
      body = buildRegistrationBatch(requests, {
        eventId,
        rules: rulesRef.current,
        // Already resolved above for the re-check; reused rather than asked
        // for twice. Undefined when there is no session, which the builder
        // turns back into '@me' so the server refuses as it does today.
        applicationUserId: who?.user_id,
        userInfo: userInfoOf(who),
      });
    } catch (err) {
      // Ours, not the server's — nothing was sent, so the modal keeps the
      // choice intact and says why.
      throw err;
    }

    const resp = await registrationApi.apiCall(
      '/arcee/application/table/save/records/',
      { method: 'POST', credentials: 'include', body: JSON.stringify(body) },
    );
    const result = await resp.json().catch(() => null);

    // No session, no registration. The visitor is sent to sign in and brought
    // straight back rather than left on a refusal they cannot act on.
    if (resp.status === 401 || resp.status === 403) {
      window.location.href = buildSignInUrl(tableSlug, recordId, pending.button.id);
      return [];
    }

    const outcome = outcomeFromBatch(resp, result, requests);

    // Written now, so a reload does not offer entry to somebody who already
    // holds their passes. Only on a real success: remembering a refused
    // checkout would withdraw the button from someone with no place.
    if (outcome.some(entry => entry.result?.ok || entry.result?.alreadyEntered)) {
      rememberEntered(
        envConfig.organizationId, envConfig.applicationId,
        record?.id ?? recordId, requests.length, who?.user_id,
      );
      setEntered(true);
    }

    return outcome;
  }, [pending, record?.id, recordId, tableSlug]);

  /**
   * CAME BACK FROM THE GATEWAY WITHOUT PAYING — TELL THE SERVER AT ONCE.
   *
   * The modal already tells the RUNNER (see EventEntryModal's pageshow note).
   * This tells the server, which is the half that frees the places. Without
   * it, a deliberate cancel left the reservation standing until the gateway
   * order lapsed, and the next buyer was refused places that nobody wanted.
   *
   * IT DECIDES NOTHING. All it does is ask the status endpoint about the one
   * payment this tab handed over. That endpoint asks the gateway whether an
   * attempt is in flight and releases only on "nobody ever tried to pay", so a
   * runner who is mid-payment in another tab, or who pressed back and then
   * went forward again and paid, is unaffected. Asking is safe; asking twice
   * is safe; not asking only costs the instant release.
   *
   * ASKED ONCE PER RETURN. The id is forgotten before the request goes out, so
   * a tab brought to the front repeatedly does not re-ask, and a stray
   * visibility change cannot turn into a stream of gateway round trips.
   */
  useEffect(() => {
    let dropped = false;

    const settleTheHandover = async () => {
      const inFlight = recallInFlight();
      if (!inFlight) return;
      forgetInFlight();
      try {
        await fetchPaymentStatus({ paymentId: inFlight.paymentId });
      } catch (e) {
        // The places come back without this; it is the speed that is lost.
        return;
      }
      // The places may have just changed hands, and the button has to agree
      // with that -- the runner is looking at it right now.
      if (!dropped) checkRegistration();
    };

    // `persisted` is the browser saying it restored this page from cache,
    // which is exactly the back-button case. The visibility handler catches
    // the rest: a tab switched back to, a mobile browser resumed.
    const onPageShow = (e) => { if (e && e.persisted) settleTheHandover(); };
    const onVisible = () => {
      if (document.visibilityState === 'visible') settleTheHandover();
    };

    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      dropped = true;
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [checkRegistration]);

  // Finish a submission the visitor started before signing in.
  useEffect(() => { resumeAfterSignIn(); }, [resumeAfterSignIn]);

  const isCompleted = useCallback(
    // Wrapped so a surprise here can never take the page down with it — and it
    // fails to "not completed", which draws the button.
    //
    // Two sources, and only one of them survives a new device. The server's own
    // record of completed actions is still consulted for anything written
    // through the action path; `entered` is this browser's memory of a batch
    // registration, which is the only trace the batch endpoint leaves. See
    // registrationMemory for why it cannot yet be read back from the data.
    button => (
      (String(button?.id) === 'register' && entered)
      || decideCompleted(button, completedKeys, record?.id)
    ),
    [completedKeys, record?.id, entered],
  );

  const onAction = useCallback(button => startAction(button, {
    tableSlug, recordId, isAuthenticated: signedIn,
    // A button with no form of its own still asks which activities — Register
    // means the same thing wherever it is pressed.
    onSubmit: b => openEntryModal({ button: b, answers: {}, payload: {} }),
  }), [tableSlug, recordId, signedIn, openEntryModal]);

  if (isLoading) return <EventDetailSkeleton />;
  if (isError) return <PublicErrorPage error={error} onRetry={refetch} />;

  // The registrations lookup could not answer. The whole page stands down
  // rather than the button alone: drawing the page without knowing whether this
  // visitor already holds a place means either offering an entry the database
  // will refuse, or hiding one they are entitled to. Retry re-asks; it does not
  // reload, so nothing else on the page is fetched twice.
  if (statusUnknown) {
    return <PublicErrorPage error={{ status: 0 }} onRetry={checkRegistration} />;
  }

  // The event under its table's own name, related blocks under the aliases the
  // builder chose. A failed block arrives as an empty list, so a flaky related
  // table costs a section rather than the page.
  const scope = { [EVENT_TABLE_SLUG]: record, ...buildRelatedScope(data.related) };

  if (Array.isArray(scope.categories)) {
    scope.categories = [...scope.categories].sort((left, right) => (
      (Number(left?.order_index) || 0) - (Number(right?.order_index) || 0)
    ));
  }

  // The currency every fee on this event is quoted in, for the entry modal.
  // `registration` is event_platform_registration's alias in the page's data
  // source; an event whose config names it otherwise resolves to null here and
  // prices render as bare numbers rather than disappearing.
  const eventCurrency = resolveEventCurrency(
    currencies, scope.registration?.currency_id,
  );

  // How entry behaves for THIS event — whether it is offered at all, what
  // status a seat is written with, and whether a pass is minted. Read from the
  // registration type's stable longcode; see registrationRules for what is
  // deliberately left to the server (the status clock, and capacity).
  const rules = registrationRules(record, scope);
  rulesRef.current = rules;

  // Buttons are built by the page now, not read from stored configuration.
  // Same four ids, same labels and destinations — see eventButtons, including
  // what re-checking the server no longer does.
  const buttons = eventButtons(record, scope, rules, { entered });

  // const titleValue = record?.[viewConfig?.field_mapping?.title_field];
  const titleValue = eventTitleOf(record, viewConfig?.field_mapping);

  return (
    // The same shell the Page Builder path puts a record template in, so the
    // page keeps the gutter and the vertical padding it had. Dropping it would
    // let the entry band run truly full-bleed — which is what its CSS was
    // written for, but NOT what the live page does today.
    <div className="container py-4">
      {actionState && actionState.status === 'done' && actionState.reveal && (
        <PublicSuccessModal
          open
          message={actionState.message}
          // Only when it really is a string: a JSON or array column here would
          // throw "Objects are not valid as a React child" and take the whole
          // page down with it.
          subtitle={typeof titleValue === 'string' ? titleValue : undefined}
          reveal={actionState.reveal}
          // Only the pass is dismissed, never the 'done' state. Clearing the
          // whole thing restores the pristine Register button, and pressing it
          // again lands on the uniqueness clash that keeps visitors out of a
          // duplicate entry.
          onClose={() => setActionState(state => ({ ...state, reveal: null }))}
        />
      )}

      <EventEntryModal
        open={Boolean(pending)}
        onClose={() => setPending(null)}
        eventTitle={typeof titleValue === 'string' ? titleValue : 'this event'}
        checkInHint={checkInOpensHint(record)}
        activities={Array.isArray(scope.categories) ? scope.categories : []}
        formatFee={value => formatAmount(value, eventCurrency)}
        onConfirm={confirmEntry}
        // Prefill only. login/check returns whatever the account already holds;
        // the runner may type something else for this payment, and nothing is
        // written back.
        contact={session}
        applicationId={envConfig.applicationId}
        eventId={record?.id ?? recordId}
        onCheckAvailability={checkAvailability}
        // Only where a seat gets a pass to put the name on — a waiting-list
        // seat has none, so a name typed for it would go nowhere.
        collectParticipants={Boolean(rules?.mintsPass)}
        // The same fact, for a different decision: a pass doubles a seat's
        // cost in batch operations, so it halves how many fit in one booking.
        // Sent separately from collectParticipants because they answer
        // different questions and one of them may yet change without the other.
        mintsPass={Boolean(rules?.mintsPass)}
      />

      <EventDetailTemplate
        scope={scope}
        currency={eventCurrency}
        recordAlias={EVENT_TABLE_SLUG}
        buttons={buttons}
        isCompleted={isCompleted}
        pendingPayment={pendingPayment}
        formState={actionState}
        onAction={onAction}
        // Only while entries are open: a registered visitor still sees their
        // entry after registration closes, but must not be offered more seats.
        onBookMore={rules?.canRegister ? openMoreSeats : undefined}
      />
    </div>
  );
}
